<script lang="ts">
  import CommentItem from './CommentItem.svelte';
  import UnreadDivider from '../Messaging/UnreadDivider.svelte';
  import type { Comment } from './api';
  import type { MessagingUser } from '../Messaging/api';
  import { Button } from '$lib/components/ui/button';

  export let comments: Comment[];
  export let readersByComment: (commentId: string) => MessagingUser[] = () => [];
  export let currentUserId: number;
  // The comment to show the "N unread" divider directly above -- see CommentThreadPanel's
  // unreadDividerId for how this is computed/held fixed.
  export let unreadDividerId: string | null = null;
  // Whether an older page of comments might still exist -- shown as a "Load older" button
  // above the list rather than an infinite-scroll trigger, since this no longer owns its
  // own scroll container (it flows inline in whatever dialog hosts it).
  export let hasMore: boolean = false;
  export let loadingMore: boolean = false;
  export let onLoadMore: () => void = () => {};
  // A sentinel element after the last comment -- the host panel uses this both as the
  // scrollIntoView target for "jump to latest" and as an IntersectionObserver root to know
  // whether the bottom is currently in view.

  export let onReply: (comment: Comment) => void = () => {};
  export let onEdit: (comment: Comment) => void = () => {};
  export let onDelete: (comment: Comment) => void = () => {};
  export let onReact: (comment: Comment, emoji: string) => void = () => {};
  export let onPin: (comment: Comment) => void = () => {};
  export let onUnpin: (comment: Comment) => void = () => {};
  export let onForward: (comment: Comment) => void = () => {};
  export let onJumpTo: (commentId: string) => void = () => {};

  // Consecutive comments from the same author, each within this many ms of the *previous* comment
  // (not the first in the run), collapse onto a single avatar/name -- a chained gap check, so a
  // long-running conversation only breaks the group where an actual gap appears.
  const GROUP_WINDOW_MS = 5 * 60 * 1000;

  $: rows = comments.map((comment, i) => {
    if (i === 0) return { comment, isInSequence: false };
    const previous = comments[i - 1];
    const isInSequence =
      previous &&
      previous.author?.id === comment.author?.id &&
      new Date(comment.created_at).getTime() - new Date(previous.created_at).getTime() <= GROUP_WINDOW_MS;
    return { comment, isInSequence };
  });
</script>

<div>
  {#if comments.length === 0}
    <div class="flex flex-col items-center justify-center gap-2 px-6 py-8 text-center">
      <i class="fa-light fa-comment-dots text-lg text-muted-foreground"></i>
      <p class="m-0 text-sm text-muted-foreground">No comments yet.</p>
    </div>
  {/if}
  {#if hasMore}
    <div class="flex justify-center py-2">
      <Button variant="outline" size="sm" on:click={onLoadMore} disabled={loadingMore}>
        {loadingMore ? 'Loading…' : 'Load older comments'}
      </Button>
    </div>
  {/if}
  {#each rows as { comment, isInSequence }, i (comment.id)}
    {#if comment.id === unreadDividerId}
      <UnreadDivider count={comments.length - i} />
    {/if}
    <CommentItem
      {comment}
      {isInSequence}
      {currentUserId}
      readers={readersByComment(comment.id)}
      {onReply}
      {onEdit}
      {onDelete}
      {onReact}
      {onPin}
      {onUnpin}
      {onForward}
      {onJumpTo}
    />
  {/each}
</div>
